/**
 * ReviewPage — full-page readable view of converted Markdown.
 * Opened in a new tab from HomePage.
 */

import React, { useEffect, useState, Children } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import { ThemeToggle } from "../components/shared/ThemeToggle";
import { TableOfContents } from "../components/review/TableOfContents";
import { useTheme } from "../hooks/useTheme";
import { slugify } from "../utils/slugify";

const STORAGE_KEY = "review_markdown";

function MdParagraph({ children, ...props }: React.HTMLAttributes<HTMLElement>) {
  const kids = Children.toArray(children);
  const allImages = kids.every(
    (child) => React.isValidElement(child) && child.type === "img"
  );
  if (allImages && kids.length > 0) {
    return <div className="md-image-group">{children}</div>;
  }
  return <p {...props}>{children}</p>;
}

const HeadingWithId =
  (level: 1 | 2 | 3) =>
  ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
    const text = Array.isArray(children) ? children.join("") : String(children ?? "");
    const id = slugify(text);
    const Tag = `h${level}` as "h1" | "h2" | "h3";
    return (
      <Tag id={id} {...props}>
        {children}
      </Tag>
    );
  };

export const ReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [markdown, setMarkdown] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setMarkdown(stored ?? "");
  }, []);

  if (markdown === null) return null;

  if (!markdown) {
    return (
      <div className="review-page review-page--empty">
        <p>{t("review.empty")}</p>
      </div>
    );
  }

  return (
    <div className="review-page">
      <header className="review-page__header">
        <h1 className="review-page__title">{t("review.title")}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ThemeToggle />
          <button className="btn btn--ghost btn--sm" onClick={() => window.print()}>
            {t("review.print")}
          </button>
        </div>
      </header>

      <div className="review-layout">
        <TableOfContents markdown={markdown} />

        <article className="review-page__content markdown-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            urlTransform={(url) => url}
            components={{
              p: MdParagraph as any,
              h1: HeadingWithId(1),
              h2: HeadingWithId(2),
              h3: HeadingWithId(3),
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                const isBlock = !props.style;
                return isBlock && match ? (
                  <SyntaxHighlighter
                    style={theme === "dark" ? tomorrow : oneLight}
                    language={match[1]}
                    PreTag="div"
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {markdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
};

export { STORAGE_KEY as REVIEW_STORAGE_KEY };

/**
 * Store markdown and open the review page in a new tab. Returns false if the
 * markdown could not be stored (e.g. localStorage quota exceeded by a large
 * image-heavy result) so the caller can inform the user instead of crashing.
 */
export function openMarkdownInReview(markdown: string): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, markdown);
  } catch {
    return false;
  }
  window.open("/review", "_blank", "noopener");
  return true;
}
